import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { defineCommand } from '../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Host } from '../../Host';
import { AccountModel } from '../account/AccountModel';
import { BoardUsageModel } from '../account/BoardUsageModel';
import { AppEvents } from '../AppEvents';
import { AppLocation } from '../AppLocation';
import { AppNavigator } from '../AppNavigator';
import { AllBoardsActiveViewModel } from '../boards/AllBoardsActiveViewModel';
import { Resolver } from '../Container';
import { BoardModel, ModelBroker, WorkspaceModel } from '../dashboard/Models';
import { fileSelectPopup } from '../popups/FileSelectPopup';
import { PopupManager } from '../popups/PopupManager';
import { openUpgradeWindow } from '../upgrade/UpgradeButton';
import { boardBuilderPopup } from './BoardBuilderPopup';
import { BoardClonePopup } from './BoardClonePopup';
import { boardCreateOverLimitDashboardNextPopup, boardCreateOverLimitPopup } from './BoardCreateOverLimitPopup';
import { BoardDeletePopup } from './BoardDeletePopup';
import { BoardMovePopup } from './BoardMovePopup';
import { BoardRenamePopup } from './BoardRenamePopup';
import { boardRestorePopup } from './BoardRestorePopup';
import { selectWorkspacePopup } from './SelectWorkspacePopup';

// This needs abstracting so that a command can "get" a board/workspace descriptor and not know what context it's in.
// Don't have time now (tm) so hack:
const shittyGetBoard = (r: Resolver, boardId: string) => (
    r.call(ModelBroker).get(BoardModel, boardId) ??
    r.call(AllBoardsActiveViewModel).items.find(x => x.id === boardId)
);

export const boardBuild = defineCommand(
    'boardBuild',
    'Board: Build...',
    async (cc, [tagId]: [string?]) => {
        await cc.call(PopupManager).invoke(boardBuilderPopup());
    },
);

export const boardClone = defineCommand(
    'boardClone',
    'Board: Clone',
    async (r, [boardId, label, location]: [string, string?, string?]) => {

        if (!boardId) return false;

        const api = r.call(ApiClient);
        const account = r.call(AccountModel);
        const evts = r.call(AppEvents);

        const board = shittyGetBoard(r, boardId);
        if (!board) return false;

        if (!label || !location) {
            const { actioned } = await r.call(PopupManager).invoke({ ui: BoardClonePopup, args: { board } });
            return actioned;
        }

        const folder = account.workspaces.flatMap(x => x.folders).find(x => x.id === location);
        const workspace = account.workspaces.find(x => !!folder ? x.folders.includes(folder) : x.id === location);

        const result = await api.exec(v2.boards.create({
            label,
            workspaceId: workspace?.id ?? null,
            folderId: folder?.id ?? null,
            source: board.id,
        }));

        // Result comes back without workspace or folder, so patch these on then emit event
        const data = {
            ...result,
            workspaceId: workspace?.id ?? null,
            folderId: folder?.id ?? null,
            pinned: false,
        };

        evts.push('board:create', { board: data });

        return true;
    }
);

export const boardCreate = defineCommand(
    'boardCreate',
    'Board: Create...',
    async (r, [source, label, type, workspaceId, folderId]: [string, string?, number?, string?, string?]) => {
        if (!source) return false;
        type = type || 1;

        const account = r.call(AccountModel);
        const location = r.call(AppLocation);
        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);
        const popups = r.call(PopupManager);
        const usage = r.call(BoardUsageModel);
        const version = account.metadata.useLegacyDashboard ? 'legacy' : 'next';

        if (usage.enabled && usage.remaining === 0) {
            if (version === 'next') {
                const { actioned, data } = await popups.invoke(boardCreateOverLimitDashboardNextPopup());
                if (actioned && !!data) {
                    openUpgradeWindow(account.id);
                }
                return false;
            }
            else {
                const { actioned, data } = await popups.invoke(boardCreateOverLimitPopup());
                if (actioned && !data) {
                    openUpgradeWindow(account.id);
                    return false;
                }
                if (!actioned || !data) return false;
            }
        }

        const hasWorkspaces = account?.workspaces?.filter(x => x.type === 'team')?.length > 0;
        workspaceId = workspaceId || location.get('workspace') || null;
        folderId = folderId || location.get('folder') || location.get('boards') || null;
        if (!folderId?.startsWith('FO')) folderId = null;

        if (account.plan.features.workspaces && hasWorkspaces && !workspaceId) {
            const { actioned, data } = await popups.invoke(selectWorkspacePopup({
                label: 'Select the workspace/folder for your new board:',
                primary: 'Create',
            }));
            if (!actioned || !data) return;
            workspaceId = data.workspaceId;
            folderId = data.folderId || null;
        }

        const result = await api.exec(v2.boards.create({
            label,
            source,
            type,
            workspaceId,
            folderId,
        }));

        evts.push('board:create', { board: { ...result, pinned: false } });

        return result.id;
    },
);

export const boardCreateAndOpen = defineCommand(
    'boardCreateAndOpen',
    'Board: Create & Open...',
    async (r, [source, label, type, workspaceId]: [string, string?, number?, string?]) => {
        const boardId = await r.call(CommandDispatcher)(boardCreate, [source, label, type, workspaceId]);
        if (boardId) {
            r.call(AppNavigator).go(`~/${boardId as any}`);
        }
    },
);

export const boardDelete = defineCommand(
    'boardDelete',
    'Board: Delete',
    async (r, [boardId, confirm]: [string, string?]) => {

        if (!boardId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);

        const board = shittyGetBoard(r, boardId);
        if (!board) return false;

        if (!confirm) {
            const { actioned } = await r.call(PopupManager).invoke({ ui: BoardDeletePopup, args: { board } });
            return actioned;
        }

        if (confirm !== 'YES') return;

        // Make the call and publish the result to the event bus
        await api.exec(v2.boards.delete({ boardId }));
        evts.push('board:delete', { boardId });

        return true;
    }
);

export const boardMove = defineCommand(
    'boardMove',
    'Board: Move',
    async (r, [boardId, toId]: [string, string]) => {

        if (!boardId) return false;

        if (!toId) {
            const { actioned } = await r.call(PopupManager).invoke({ ui: BoardMovePopup, args: { boardId } });
            return actioned;
        }

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);

        // Make the call and publish the result to the event bus
        const result = await api.exec(v2.boards.move({ boardId, toId }));
        evts.push('board:move', result);

        return true;
    }
);

export const boardSetPinned = defineCommand(
    'boardSetPinned',
    'Board: Set Pinned',
    async (r, [boardId, value]: [string, boolean]) => {

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);
        const workspace = r.call(WorkspaceModel, false);

        if (!boardId || !workspace) return false;

        // Make the call and publish the result to the event bus
        await api.exec(v2.boards.setPinned({ boardId, workspaceId: workspace.id, value }));
        evts.push('board:patch', { board: { id: boardId, pinned: value } });

        return true;
    }
);

export const boardRename = defineCommand(
    'boardSetName',
    'Board: Set Name',
    async (r, [boardId, value]: [string, string]) => {

        if (!boardId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);

        const board = shittyGetBoard(r, boardId);
        if (!board) return false;

        if (!value) {
            const { actioned } = await r.call(PopupManager).invoke({ ui: BoardRenamePopup, args: { board } });
            return actioned;
        }

        await api.exec(v2.boards.patch(boardId, { name: value }));
        evts.push('board:patch', { board: { id: boardId, label: value } });

        return true;
    },
);

export const boardBackup = defineCommand(
    'boardBackup',
    'Board: Backup...',
    (r, [boardId]: [string]) => {

        window.open(`${Host.baseUrl}/api/v2/boards.backup?boardId=${boardId}`);
    },
);

export const boardRestore = defineCommand(
    'boardRestore',
    'Board: Restore...',
    async r => {

        const location = r.call(AppLocation);
        const popups = r.call(PopupManager);

        const { data: files } = await popups.invoke(fileSelectPopup(20));
        if (!files || !files.length) return;

        const toId = location.get('folder') || location.get('workspace');
        await popups.invoke(boardRestorePopup(files, toId));

        // evts.push('board:create', { board: { ...result, pinned: false } });
        window.location.reload();
        // navigator.go(`~/${board.id}`);
    },
);

